<script setup lang="ts">
import { DropdownItemButton, type SelectLine } from '@/objects';
import { ContentComp, ToolbarComp, DateComp, InputSelect, ValidatedText } from '..';
import { useLoaders, useTaskLoader } from '@/composeables';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '../Form';
import { MergeParticipantsRequest, type Participant } from '@/objects/generated';
import { ref, watch } from 'vue';
import { useCommonStore } from '@/stores';
import { ParticipantDao } from '@/dao';

const { LoadState, AddLoader } = useTaskLoader();
useLoaders(AddLoader, ['IDTYPES']);

const IDTypes = useCommonStore().IDTypes;

const props = defineProps<{
    participants?: Array<Participant>;
    main?: Participant | null;
}>();

const emit = defineEmits<{
    (e: 'done', merged: boolean): void;
}>();

const closeAction = DropdownItemButton.create({
    buttonType: 'Close',
    action: () => {
        emit('done', false);
    },
    icon: 'fa-arrow-left',
    translationkey: 'general.back',
});

const confirmAction = DropdownItemButton.create({
    buttonType: 'Primary',
    action: async () => {
        await AddLoader(async () => {
            // Merge the participants
            const req = MergeParticipantsRequest.FromJsValues({
                MasterID: props.main!.ID,
                ParticipantIdsToMerge: props.participants!.filter((x) => x.ID != props.main!.ID).map((x) => x.ID),
                ParticipantIDIds: [...participantIDs.value.values()].map((v) => v.rowid),
            });

            await ParticipantDao.Merge(req);
        }, 'duplicates.merging');
        console.log('Merged participants');
        emit('done', true);
    },
    icon: 'fa-check',
    translationkey: 'general.confirm',
});

type ParticipantIdType = number;
const participantIDs = ref(new Map<ParticipantIdType, { rowid: number; values: SelectLine[] }>());
watch(
    () => props.participants,
    (newParticipants) => {
        const map = new Map<ParticipantIdType, { rowid: number; values: SelectLine[] }>();
        for (const p of newParticipants ?? []) {
            for (const val of p.ParticipantIDs ?? []) {
                if (!map.has(val.ParticipantIDTypeID)) {
                    map.set(val.ParticipantIDTypeID, { rowid: val.ID, values: [] });
                }
                const valuesList = map.get(val.ParticipantIDTypeID)!;

                // If we already have one with this ID (the text in the id field), we dont add the same again
                // These will have two different rowids, but we will end up only keeping one of them, so it doesnt matter which one we select
                if (valuesList.values.some((x) => x.text === val.Value)) {
                    continue;
                }
                valuesList.values.push({ text: val.Value ?? '', value: val.ID });
            }
        }
        participantIDs.value = map;
    },
    { immediate: true },
);
</script>
<template>
    <ContentComp
        :load-state="LoadState"
        class="h-100"
    >
        <div class="d-flex flex-column h-100">
            <ToolbarComp
                class="mb-2"
                :actions="[confirmAction]"
                :rightActions="[closeAction]"
            >
                <template #middle>
                    <span></span>
                </template>
            </ToolbarComp>
            <div class="d-flex h-100">
                <div class="flex-grow-1">
                    <h4>{{ $tm('duplicates.confirm_merge') }}</h4>
                    <FormLayout
                        :label-width="120"
                        v-if="main"
                    >
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.name') }}</FormLayoutLabel>
                            <div>{{ main.Firstname }} {{ main.Surname }}</div>
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.birthdate') }}</FormLayoutLabel>
                            <DateComp
                                :value="main.Birthday"
                                format="DATE"
                            />
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.email') }}</FormLayoutLabel>
                            <div>{{ main.Email }}</div>
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.username') }}</FormLayoutLabel>
                            <div>{{ main.Username }}</div>
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.address') }}</FormLayoutLabel>
                            <div>{{ main.Address1 }} {{ main.Address2 }}</div>
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.postalcode') }}</FormLayoutLabel>
                            <div>{{ main.PostalNumber }}</div>
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.city') }}</FormLayoutLabel>
                            <div>{{ main.City }}</div>
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <FormLayoutLabel>{{ $tm('duplicates.lastevent') }}</FormLayoutLabel>
                            <div>{{ main.LastEvent }}</div>
                        </FormLayoutRow>
                    </FormLayout>
                </div>
                <div class="h-100 d-flex flex-column">
                    <h4 class="text-end">{{ $tm('duplicates.ids') }}</h4>
                    <div class="flex-grow-1 overflow-auto pe-3">
                        <FormLayout :label-width="120">
                            <FormLayoutRow
                                v-for="[idtypeid, val] in participantIDs"
                                :key="idtypeid"
                            >
                                <FormLayoutLabel>{{ IDTypes.find((x) => x.ID == idtypeid)!.Name }}</FormLayoutLabel>
                                <InputSelect
                                    v-if="val.values.length > 1"
                                    :datasource="{ source: val.values }"
                                    v-model="val.rowid"
                                />
                                <ValidatedText
                                    v-else-if="val.values.length == 1"
                                    :value="val.values[0].text"
                                />
                                <div v-else>-</div>
                            </FormLayoutRow>
                        </FormLayout>
                    </div>
                </div>
            </div>
        </div>
    </ContentComp>
</template>
